import {
  useQueryScheduledUpgradeWorkspaces,
  useMutationUpgradeWorkspace,
  useMutationUpdateWorkspace,
  useMutationBulkUpgradeWorkspaces,
} from '@/pages/workgroup/hook';
import { WorkspaceT } from '@/pages/workgroup/type';
import { Button } from '@tigergraph/app-ui-lib/button';
import { Popover } from '@tigergraph/app-ui-lib/popover2';
import { Delete } from 'baseui/icon';
import { MdCampaign } from 'react-icons/md';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { useState } from 'react';
import { ChevronDown } from 'baseui/icon';
import { ButtonGroup } from '@tigergraph/app-ui-lib/button-group';
import { expand } from 'inline-style-expand-shorthand';
import { SpaceIcon } from '@/pages/home/icons';
import { getErrorMessage } from '@/utils/utils';
import toast from 'react-hot-toast';
import { useWorkspaceContext } from '@/contexts/workspaceContext';

function WorkspaceUpgradeItem({ ws }: { ws: WorkspaceT }) {
  const [css, theme] = useStyletron();
  const { mutateAsync: upgradeWorkspace, isLoading: upgrading } = useMutationUpgradeWorkspace();
  const { mutateAsync: updateWorkspace, isLoading: updating } = useMutationUpdateWorkspace();

  return (
    <div
      className={css({
        border: `1px solid ${theme.colors['border.tertiary']}`,
        borderRadius: '2px',
        padding: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      })}
    >
      <div
        className={css({
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          color: theme.colors['text.secondary'],
        })}
      >
        <SpaceIcon />
        <span>{ws.workgroup_name}/</span>
        <span className={css({ color: theme.colors['text.primary'] })}>{ws.name}</span>
        <div className={css({ marginLeft: '4px' })}>
          ({ws.maintenance_window_hour_of_day}:00 {ws.maintenance_window_time_zone})
        </div>
      </div>
      <div className={css({ display: 'flex', gap: '8px' })}>
        <Button
          kind="secondary"
          disabled={updating}
          onClick={() => {
            const promise = updateWorkspace({
              group_id: ws.workgroup_id,
              space_id: ws.workspace_id,
              data: { update_strategy: 'update_strategy_never' },
            });
            toast.promise(promise, {
              loading: 'Disabling upgrade...',
              success: 'Upgrade disabled successfully!',
              error: (err) => `Failed to disable upgrade: ${getErrorMessage(err as Error)}`,
            });
          }}
        >
          Disable Upgrade
        </Button>
        <Button
          kind="secondary"
          disabled={upgrading}
          onClick={() => {
            const promise = upgradeWorkspace({
              workgroup_id: ws.workgroup_id,
              workspace_id: ws.workspace_id,
              scheduled: false,
            });
            toast.promise(promise, {
              loading: 'Starting upgrade...',
              success: 'Workspace upgrade started successfully!',
              error: (err) => `Failed to start upgrade: ${getErrorMessage(err as Error)}`,
            });
          }}
        >
          Upgrade Now
        </Button>
      </div>
    </div>
  );
}

// Banner component to display scheduled workspace upgrades
export default function WorkspaceUpgradeBanner() {
  const { workspaces: allWorkspaces } = useWorkspaceContext();
  const [css, theme] = useStyletron();
  const [isVisible, setIsVisible] = useState(() => {
    // Check sessionStorage for existing preference, default to true if not found
    const stored = sessionStorage.getItem('workspaceUpgradeBannerVisible');
    return stored === null ? true : stored === 'true';
  });
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  // Fetch scheduled upgrade workspaces
  let { data, isLoading } = useQueryScheduledUpgradeWorkspaces();

  // Bulk upgrade mutation
  const { mutateAsync: bulkUpgradeWorkspaces, isLoading: bulkUpgrading } = useMutationBulkUpgradeWorkspaces();

  // Do not render if loading, no data, or banner is hidden
  if (isLoading || !data || !data.Result || data.Result.length === 0 || !isVisible) return null;

  let workspaces = data.Result;
  workspaces.forEach((ws) => {
    ws.workgroup_name = allWorkspaces.find((w) => w.workgroup_id === ws.workgroup_id)?.workgroup_name || '';
  });

  // filter out workspaces scheduled with new version
  workspaces = workspaces.filter((ws) => ws.auto_upgrade_state === 'scheduled');

  if (workspaces.length === 0) return null;

  // Handle bulk upgrade
  const handleBulkUpgrade = async () => {
    const workspaceData = workspaces.map((ws) => ({
      workgroup_id: ws.workgroup_id,
      workspace_id: ws.workspace_id,
      workgroup_name: ws.workgroup_name,
      workspace_name: ws.name,
    }));

    const promise = bulkUpgradeWorkspaces({ workspaces: workspaceData });
    toast.promise(promise, {
      loading: `Starting upgrade for ${workspaces.length} workspace${workspaces.length > 1 ? 's' : ''}...`,
      success: (results) => {
        const successCount = results.filter((result) => !result.Error).length;
        const errorCount = results.filter((result) => result.Error).length;

        if (errorCount === 0) {
          return `Successfully started upgrade for all ${workspaces.length} workspace${
            workspaces.length > 1 ? 's' : ''
          }!`;
        } else if (successCount === 0) {
          return `Failed to start upgrade for any workspace. Please try again.`;
        } else {
          return `Started upgrade for ${successCount} workspace${successCount > 1 ? 's' : ''}, ${errorCount} failed.`;
        }
      },
      error: (err) => `Failed to start bulk upgrade: ${getErrorMessage(err as Error)}`,
    });
  };

  const popoverContent = (
    <div className={css({ display: 'flex', flexDirection: 'column', gap: '13px', fontSize: '14px' })}>
      <div className={css({ fontWeight: 700, fontSize: '16px' })}>Workspace Upgrade</div>
      <div className={css({ color: theme.colors['text.secondary'] })}>
        You have {workspaces.length} workspaces with scheduled maintenance in the next hour. Please complete all
        necessary tasks before the scheduled start times.
      </div>
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: '13px',
          maxHeight: '300px',
          overflowY: 'auto',
        })}
      >
        {workspaces.map((ws) => (
          <WorkspaceUpgradeItem key={ws.workspace_id} ws={ws} />
        ))}
      </div>
    </div>
  );

  return (
    <div
      className={css({
        background: theme.colors['background.warning.subtler'],
        color: theme.colors['notification.text'],
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      })}
    >
      <MdCampaign size={20} className={css({ color: theme.colors['background.brand.bold'] })} />
      <div style={{ marginRight: 'auto' }}>
        You have {workspaces.length} workspace{workspaces.length > 1 ? 's' : ''} with scheduled maintenance in the next
        hour. Please complete all necessary tasks before the scheduled start times.
      </div>
      <div
        className={css({
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          flexShrink: 0,
        })}
      >
        <div style={{ position: 'relative' }}>
          <ButtonGroup>
            <Button onClick={handleBulkUpgrade} disabled={bulkUpgrading}>
              Upgrade Now
              <span
                className={css({
                  marginLeft: '4px',
                  color: '#fff',
                  height: '16px',
                  minWidth: '16px',
                  padding: '0 4px',
                  borderRadius: '50%',
                  backgroundColor: theme.colors['background.danger.subtle'],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                })}
              >
                {workspaces.length}
              </span>
            </Button>
            <Button
              shape="square"
              onClick={() => {
                setIsPopoverOpen(!isPopoverOpen);
              }}
            >
              <ChevronDown size={20} />
            </Button>
          </ButtonGroup>
          <Popover
            isOpen={isPopoverOpen}
            onClickOutside={() => {
              setTimeout(() => {
                setIsPopoverOpen(false);
              }, 200);
            }}
            onEsc={() => {
              setTimeout(() => {
                setIsPopoverOpen(false);
              }, 200);
            }}
            placement="bottomRight"
            content={popoverContent}
            overrides={{
              Body: {
                style: {
                  maxWidth: '720px',
                  zIndex: 1000,
                },
              },
              Inner: {
                style: {
                  ...expand({
                    border: 0,
                    padding: '16px',
                  }),
                },
              },
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '24px',
                height: '100%',
                pointerEvents: 'none',
              }}
            />
          </Popover>
        </div>
        <Button
          size="compact"
          kind="text"
          shape="square"
          onClick={() => {
            setIsVisible(false);
            sessionStorage.setItem('workspaceUpgradeBannerVisible', 'false');
          }}
        >
          <Delete size={20} />
        </Button>
      </div>
    </div>
  );
}
